/**
 * Background-location service state: the manual override, whether the service is
 * up, its health, and any graceful-degradation warning.
 *
 * The decision of WHEN to run the service is pure (see safety/backgroundService);
 * this store holds the bits of state that decision and the UI need. The manual
 * override and the one-time battery-guidance flag are persisted so they survive a
 * reload; the live status is not.
 */
import { create } from 'zustand';
import type { DeviceGnssStatus } from '@/gnss/deviceSource';

const KEY = 'acp.bgservice.v1';

interface Persisted {
  manualOverride: boolean;
  batteryGuidanceShown: boolean;
}

function loadPersisted(): Persisted {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(KEY) : null;
    if (!raw) return { manualOverride: false, batteryGuidanceShown: false };
    const d = JSON.parse(raw) as Partial<Persisted>;
    return {
      manualOverride: !!d.manualOverride,
      batteryGuidanceShown: !!d.batteryGuidanceShown,
    };
  } catch {
    return { manualOverride: false, batteryGuidanceShown: false };
  }
}

interface BackgroundServiceState {
  /** User forced the service on regardless of what's armed. Persisted. */
  manualOverride: boolean;
  setManualOverride: (on: boolean) => void;

  /** The service + background watch are currently up. */
  serviceRunning: boolean;
  setServiceRunning: (on: boolean) => void;

  /**
   * Latched true when background LOCATION permission was denied, so the runner
   * stops auto-retrying (which would loop) and stays foreground-only for this
   * armed session. Cleared when everything is disarmed, so the next arm tries
   * again from scratch.
   */
  deniedLatch: boolean;
  setDeniedLatch: (on: boolean) => void;

  /** Health of the background feed (mirrors the device status vocabulary). */
  status: DeviceGnssStatus;
  message: string | null;
  setStatus: (status: DeviceGnssStatus, message?: string | null) => void;

  /**
   * True when we WANTED the background service but a permission was denied, so
   * we've fallen back to foreground-only. The UI shows a clear warning. Cleared
   * when the service comes up cleanly.
   */
  degraded: boolean;
  degradedReason: string | null;
  /**
   * Optional headline and one-tap action for the warning. 'notificationSettings'
   * puts an "Open notification settings" button on the banner, because a warning
   * that names a fix two menus deep is a warning most people read and forget.
   */
  degradedTitle: string | null;
  degradedAction: 'notificationSettings' | 'batteryOptimisation' | null;
  setDegraded: (
    degraded: boolean,
    reason?: string | null,
    opts?: { title?: string | null; action?: 'notificationSettings' | 'batteryOptimisation' | null },
  ) => void;

  /**
   * The user tapped the degrade warning away. While this is set, the runner's
   * re-assertions of `degraded` are ignored, so the banner does not immediately
   * come back on the next service (re)start with the same permission still denied
   * (which is what made it feel un-dismissable). Session-only, not persisted;
   * reset by resetDegradeDismissed when the background need fully clears, so a
   * genuinely fresh arm can warn again.
   */
  degradedDismissed: boolean;
  dismissDegraded: () => void;
  resetDegradeDismissed: () => void;

  /**
   * Whether we've shown the one-time "allow background location / turn off
   * battery optimisation" guidance. Persisted so we don't nag on every arm.
   */
  batteryGuidanceShown: boolean;
  markBatteryGuidanceShown: () => void;
}

function persist(s: Persisted): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* ignore quota / private-mode / SSR */
  }
}

const initial = loadPersisted();

export const useBackgroundServiceStore = create<BackgroundServiceState>((set, get) => ({
  manualOverride: initial.manualOverride,
  setManualOverride: (manualOverride) => {
    persist({ manualOverride, batteryGuidanceShown: get().batteryGuidanceShown });
    set({ manualOverride });
  },

  serviceRunning: false,
  setServiceRunning: (serviceRunning) => set({ serviceRunning }),

  deniedLatch: false,
  setDeniedLatch: (deniedLatch) => set({ deniedLatch }),

  status: 'idle',
  message: null,
  setStatus: (status, message = null) => set({ status, message }),

  degraded: false,
  degradedReason: null,
  degradedTitle: null,
  degradedAction: null,
  degradedDismissed: false,
  // Setting degraded=true is ignored once the user has dismissed it this session,
  // so a per-fix/per-restart re-assertion can't nag. Clearing (false) always works.
  setDegraded: (degraded, degradedReason = null, opts) =>
    set((st) =>
      degraded && st.degradedDismissed
        ? {}
        : {
            degraded,
            degradedReason,
            degradedTitle: opts?.title ?? null,
            degradedAction: opts?.action ?? null,
          },
    ),
  dismissDegraded: () => set({ degraded: false, degradedDismissed: true }),
  resetDegradeDismissed: () => set({ degradedDismissed: false }),

  batteryGuidanceShown: initial.batteryGuidanceShown,
  markBatteryGuidanceShown: () => {
    persist({ manualOverride: get().manualOverride, batteryGuidanceShown: true });
    set({ batteryGuidanceShown: true });
  },
}));
